/* ヘッダーの受取場所/日時 表示 */

/**
 * @typedef {Object} LocalizedName
 * @property {string} jaJP
 * @property {string} enUS
 * @property {string} koKR
 * @property {string} zhCN
 * @property {string} zhTW
 */

/**
 * @typedef {Object} PickupLocationAddress
 * @property {string} address1
 * @property {string} address2
 */

/**
 * @typedef {Object} CartAttributes
 * @property {LocalizedName} pickupLocation - 受取場所名（全言語）
 * @property {string} pickupLocationId - 受取場所ID（branch.id）
 * @property {string} pickupDate - 受取日 (YYYY-MM-DD)
 * @property {string} pickupTime - 受取時間目安 (HH:MM)
 * @property {string} pickupTimeRange - 受取可能時間 (HH:MM - HH:MM)
 * @property {PickupLocationAddress} pickupLocationAddress - 受取場所住所
 * @property {string} selectedLocale - 選択言語（例: 'ja-JP', 'ko-KR'）
 */

/**
 * @typedef {Object} DisplayPickupLocationData
 * @property {string} pickupLocation - 受取場所名 (表示用)
 * @property {string} pickupDate - 受取日 (表示用)
 * @property {string} pickupTime - 受取時間目安 (表示用)
 */

(() => {
  "use strict";

  /**
   * カート属性を更新
   * @param {CartAttributes} parsedData - localStorage から取得した受取場所データ
   * @returns {Promise<boolean>} 更新成功かどうか
   */
  async function updateCartAttributes(parsedData) {
    try {
      // 注文完了で新しくなったカートへ、受取場所の選択（tfo_shopify_custom_app が localStorage に保存）を戻す
      // カートのチェックアウトの判定（templates/cart.json）が必須にする属性はすべて戻す（qa FN-104）
      const updateAttributes = {
        attributes: {
          pickupLocation: parsedData.pickupLocation || '',
          pickupLocale: parsedData.pickupLocale || '',
          pickupLocationId: parsedData.pickupLocationId || '',
          pickupDate: parsedData.pickupDate || '',
          pickupTime: parsedData.pickupTime || '',
          pickupTimeJp: parsedData.pickupTimeJp || '',
          pickupTimeRange: parsedData.pickupTimeRange || '',
          pickupLocationAddress: {
            address1: parsedData.pickupLocationAddress?.address1 || '',
            address2: parsedData.pickupLocationAddress?.address2 || ''
          },
          nationality: parsedData.nationality || '',
        },
      };

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateAttributes),
      });

      if (!updateResponse.ok) {
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      }

      return true;
    } catch (error) {
      console.error('Failed to update cart attributes:', error);
      return false;
    }
  }

  /**
   * localStorage から受取場所データを取得し、必要に応じて cart を更新
   * @param {CartAttributes} cartAttributes - 現在の cart データ
   * @returns {Promise<CartAttributes>} 更新後のデータ
   */
  async function handleLocalStoragePickupData(cartAttributes) {
    const localStorageData = localStorage.getItem("pickupLocationAttributes");
    if (!localStorageData) {
      return cartAttributes;
    }

    let parsedData;
    try {
      parsedData = JSON.parse(localStorageData);
    } catch (error) {
      console.error("Failed to parse localStorage data:", error);
      return cartAttributes;
    }

    if (!parsedData.pickupDate) {
      return cartAttributes;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pickupDate = new Date(parsedData.pickupDate);
    pickupDate.setHours(0, 0, 0, 0);
    // 当日を含め過去日の場合は、localStorageのデータを削除
    if (pickupDate <= today) {
      localStorage.removeItem("pickupLocationAttributes");
      return cartAttributes;
    }

    const updateSuccess = await updateCartAttributes(parsedData);
    if (updateSuccess) {
      return {
        pickupLocation: parsedData.pickupLocation,
        pickupLocationId: parsedData.pickupLocationId,
        pickupDate: parsedData.pickupDate,
        pickupTime: parsedData.pickupTime,
        pickupTimeRange: parsedData.pickupTimeRange,
        pickupLocationAddress: parsedData.pickupLocationAddress,
        selectedLocale: parsedData.pickupLocale,
      };
    } else {
      console.error("Failed to update cart");
      return cartAttributes;
    }
  }

  /**
   * カート属性を取得 (API/ローカルストレージ)
   * @returns {Promise<CartAttributes>}
   */
  async function fetchCartAttributes() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const attributes = data.attributes || {};

      let cartAttributes = {
        pickupLocation: attributes.pickupLocation || "",
        pickupLocationId: attributes.pickupLocationId || "",
        pickupDate: attributes.pickupDate || "",
        pickupTime: attributes.pickupTime || "",
        pickupTimeRange: attributes.pickupTimeRange || "",
        pickupLocationAddress: attributes.pickupLocationAddress || {},
        selectedLocale: attributes.pickupLocale || "",
      };

      if (attributes.pickupLocationId) {
        return cartAttributes;
      }

      return await handleLocalStoragePickupData(cartAttributes);
    } catch (error) {
      console.error("Failed to fetch cart attributes:", error);
      return null;
    }
  }
  /**
   * pickupLocationハッシュ文字列からpickupLocaleに対応する受取場所名を取り出す
   * @param {string} pickupLocation - Rubyハッシュ形式の文字列（例: {"jaJP" => "東京店", "enUS" => "Tokyo"}）
   * @param {string} pickupLocale - 選択言語（例: 'ja-JP', 'ko-KR'）
   * @returns {string} 受取場所名
   */
  function resolvePickupLocationName(pickupLocation, pickupLocale) {
    if (!pickupLocation) return '';

    // 'ja-JP' → 'jaJP' の形式に変換
    const localeKey = pickupLocale ? pickupLocale.replace('-', '') : 'jaJP';

    // オブジェクト形式（LocalizedName）の場合は直接キーで取得
    if (typeof pickupLocation === 'object') {
      return pickupLocation[localeKey] || pickupLocation['enUS'] || pickupLocation['jaJP'] || '';
    }

    try {
      const cleaned = pickupLocation.replace(/[{}]/g, '');

      const entries = cleaned.split(',')
        .map(pairString => pairString.split('=>').map(s => s.trim().replace(/^"|"$/g, '')))
        .filter(([k, v]) => k && v && v !== 'nil');

      const map = Object.fromEntries(entries);
      return map[localeKey] || map['enUS'] || map['jaJP'] || '';
    } catch {
      return pickupLocation;
    }
  }

  /**
   * CartAttributes から表示用データへ変換
   * @param {CartAttributes} cartAttributes
   * @returns {DisplayPickupLocationData}
   */
  function toDisplayPickupLocationData(cartAttributes) {
    return {
      pickupLocation: resolvePickupLocationName(cartAttributes.pickupLocation, cartAttributes.selectedLocale),
      pickupDate: cartAttributes.pickupDate,
      pickupTime: cartAttributes.pickupTime,
    };
  }

  /**
   * 日付文字列を表示用にフォーマット（YYYY-MM-DD -> MM/DD）
   * @param {string} dateString - YYYY-MM-DD形式などの日付文字列
   * @returns {string} MM/DD形式の文字列
   */
  function formatDateForDisplay(dateString) {
    if (!dateString) return "";

    const parts = dateString.split("-");
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}`;
    }

    return dateString.replace(/-/g, "/");
  }

  /**
   * 受取場所・日時の情報をUIに反映
   * @param {HTMLElement} container - コンポーネントのコンテナ要素
   * @param {CartAttributes} cartAttributes - カート属性データ
   */
  function setupPickupLocationDisplay(container, cartAttributes) {
    const pickupLocationName = container.querySelector("._tfo_PickupLocation-name");
    const pickupLocationDate = container.querySelector("._tfo_PickupLocation-date");
    const pickupLocationTime = container.querySelector("._tfo_PickupLocation-time");

    if (!pickupLocationName || !pickupLocationDate || !pickupLocationTime) return;

    const displayPickupLocationData = toDisplayPickupLocationData(cartAttributes);
    if(displayPickupLocationData) {
      const { pickupLocation, pickupDate, pickupTime } = displayPickupLocationData;

      const pickupLocationSummary = container.querySelector(
        "._tfo_PickupLocation-summary"
      );

      if (pickupLocation && pickupDate && pickupTime) {
        if (pickupLocationSummary) {
          pickupLocationSummary.classList.add("is-selected");
        }
        pickupLocationName.innerText = pickupLocation;
        pickupLocationName.title = pickupLocation;
        pickupLocationDate.innerText = formatDateForDisplay(pickupDate);
        pickupLocationTime.innerText = pickupTime;
      }
    } else {
      pickupLocationName.innerText = container.dataset.tfoErrorText
      pickupLocationDate.innerText = "";
      pickupLocationTime.innerText = "";
    }
  }

  /**
   * ヘルプポップアップのイベントを設定
   * @param {HTMLElement} container - コンポーネントのコンテナ要素
   */
  function setupHelpPopup(container) {
    const helpToggle = container.querySelector("._tfo_PickupLocation-helpButton");
    const helpClose = container.querySelector(
      "._tfo_PickupLocation-helpPopupClose"
    );
    const helpPopup = container.querySelector("._tfo_PickupLocation-helpPopup");

    if (!helpToggle || !helpPopup) return;

    const toggleHelp = (forceClose = false) => {
      const isVisible = helpPopup.style.display === "block";
      helpPopup.style.display = forceClose ? "none" : isVisible ? "none" : "block";
    };

    helpToggle.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleHelp();
    });

    helpClose.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleHelp(true);
    });
  }

  /**
   * UIの初期化
   */
  async function init() {
    const containers = document.querySelectorAll("._tfo_PickupLocation");
    if (containers.length === 0) return;

    const cartAttributes = await fetchCartAttributes();

    containers.forEach((container) => {
      if (cartAttributes) {
        setupPickupLocationDisplay(container, cartAttributes);
      }
      setupHelpPopup(container);
      container.style.display = "flex";
    });

    // PickupLocationのサイズ変更に伴い、スクロールナビゲーションの表示有無を再計算する
    document.querySelectorAll('scrollable-navigation').forEach((nav) => {
      if (typeof nav.checkNav === 'function') {
        nav.checkNav();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
